/**
 * Iter-185.B (Sprint B · audit item H4) — "As seen in" press logo strip.
 *
 * Bankrate, NerdWallet, Policygenius, and The Zebra all surface a press
 * / "As seen in" logo strip below the hero. It's a key E-E-A-T signal
 * that affiliate-network reviewers (Bankrate Publisher Portal, Impact,
 * CJ) explicitly look for during inbound publisher applications.
 *
 * This component scaffolds the surface but ships in a NEUTRAL state
 * until the owner provides real press placements. We do NOT render
 * placeholder logos or invented press claims — that's a truthfulness
 * violation per the iter-1 principles documented in
 * /app/docs/CARSAVR_DESIGN_SYSTEM.md §1.
 *
 * USAGE:
 *   When the owner secures real press placements, populate the `LOGOS`
 *   array with `{ name, src, href }` entries and remove the early-return.
 *   The grid layout below is production-ready — desktop = 6-col,
 *   mobile = 3-col, all logos at slate-400 grayscale + hover-to-color.
 *
 * Until real placements exist, the component returns `null` so the
 * homepage scroll rhythm is unaffected.
 *
 * TARGET PLACEMENTS (owner action — secure 4-8 of these):
 *   - Forbes Advisor
 *   - MarketWatch
 *   - Yahoo Finance
 *   - AP News / Associated Press
 *   - Bloomberg Wealth
 *   - Insurance Journal
 *   - Investopedia
 *   - U.S. News & World Report Money
 *
 * Owner secures placement via HARO submissions or direct outreach;
 * once a placement runs, the owner drops the logo SVG into
 * `/app/frontend/public/press-logos/` and adds an entry to LOGOS below.
 */

// When owner provides real press placements, populate this array.
// Format: { name: "Forbes Advisor", src: "/press-logos/forbes.svg", href: "https://forbes.com/article-url" }
const LOGOS = [];

export default function PressStrip() {
  // Truthfulness gate: never render placeholder logos. If LOGOS is
  // empty, return null so the homepage scroll rhythm is unaffected.
  if (LOGOS.length === 0) return null;

  return (
    <section
      data-testid="press-strip"
      aria-label="Press placements"
      className="bg-white border-y border-slate-200 py-8 lg:py-10"
    >
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        <p
          data-testid="press-strip-eyebrow"
          className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 text-center mb-5"
        >
          As featured in
        </p>
        <div
          data-testid="press-strip-logos"
          className="grid grid-cols-3 md:grid-cols-6 gap-x-8 gap-y-6 items-center justify-items-center"
        >
          {LOGOS.map((logo) => {
            const inner = (
              <img
                src={logo.src}
                alt={logo.name}
                className="max-h-7 lg:max-h-8 w-auto object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition"
                loading="lazy"
              />
            );
            return logo.href ? (
              <a
                key={logo.name}
                href={logo.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`press-strip-logo-${logo.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="block"
              >
                {inner}
              </a>
            ) : (
              <div
                key={logo.name}
                data-testid={`press-strip-logo-${logo.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
